import _ from 'lodash'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useDebouncedCallback } from 'use-debounce'

import { SpecialityAutocompleteOption } from '@/components/ui/autocomplete/SpecialitiesAutoComplete'

import { useRequestHandler } from '@/hooks/useRequestHandler'

import { useAppDispatch, useAppSelector } from '@/redux/rootReducer'
import { getConsultationReasons, selectConsultationReasons } from '@/redux/reducers/consultation.reducer'
import {
  createSpecialityOption,
  getSpecialityOptions,
  latestCreatedSpecialityOptionIdSelector,
  selectSpecialityOptionCreateRequestTracker,
  selectSpecialityOptions,
} from '@/redux/reducers/speciality-option.reducer'

import { CreateConsultationStepperContext } from '../CreateConsultationStepper'
import SelectSpecialityStep, { FormValues } from '../components/SelectSpecialityStep'
import CreateConsultationStepLayout from '../components/CreateConsultationStepLayout'

export default function SelectSpecialityStepContainer() {
  const dispatch = useAppDispatch()

  const { setValue, values } = useContext(CreateConsultationStepperContext)

  const consultationReasons = useAppSelector(selectConsultationReasons)

  const reasons = useMemo(() => _.map(consultationReasons, 'name'), [consultationReasons])
  const specialities = useAppSelector(selectSpecialityOptions)

  const [selectedSpeciality, setSelectedSpeciality] = useState<SpecialityAutocompleteOption | null>(
    values.speciality
      ? {
          _id: '',
          name: values.speciality,
          code: '',
        }
      : null
  )

  const [defaultSet, setDefaultSet] = useState(false)

  const handleSetSpecialityValue = useCallback(
    (value: SpecialityAutocompleteOption | null | undefined) => {
      if (!value) return
      setSelectedSpeciality(value)
      if (value) {
        setValue('speciality', value.name)
      } else {
        setValue('speciality', undefined)
      }
    },
    [setValue]
  )

  useEffect(() => {
    dispatch(getConsultationReasons())
    dispatch(getSpecialityOptions())
  }, [dispatch])

  useEffect(() => {
    if (!defaultSet && specialities.length > 0) {
      setDefaultSet(true)
      const sp = _.find(specialities, ['selectByDefault', true])
      handleSetSpecialityValue(sp)
    }
  }, [specialities, defaultSet, handleSetSpecialityValue])

  /** Specialities AutoComplete special sauce 🔥 */

  const latestCreatedSpeciality = useAppSelector(latestCreatedSpecialityOptionIdSelector)

  const { dispatch: createSpecialityOptionDispatch } = useRequestHandler({
    onSuccess: () => {
      // Reload the speciality options after successful creation
      dispatch(getSpecialityOptions({}))
      // Add the newly created speciality to the formik field
      const value = specialities.find((speciality) => speciality._id === latestCreatedSpeciality) as SpecialityAutocompleteOption
      if (value) {
        handleSetSpecialityValue(value)
      }
    },
    onError: () => {},
    requestTrackerSelector: selectSpecialityOptionCreateRequestTracker,
  })

  const handleInputChange = useDebouncedCallback((newInputValue: string) => {
    dispatch(getSpecialityOptions({ smartSearch: newInputValue }))
  }, 500)

  const handleSpecialityChange = (
    event: any,
    newValue: (string | SpecialityAutocompleteOption)[] | SpecialityAutocompleteOption | string | null,
    reason: string
  ) => {
    if (Array.isArray(newValue)) {
      return
    }
    if (reason === 'removeOption' || reason === 'clear') {
      handleSetSpecialityValue(null)
      return
    }
    if (reason === 'createOption') {
      // find the string in the newValue array
      if (!newValue) {
        return
      }
      if (!(typeof newValue === 'string')) {
        return
      }
      createSpecialityOptionDispatch(
        createSpecialityOption({
          name: newValue,
          code: 'CREATED.' + newValue.toLowerCase().replace(/ /g, '-'),
        })
      )
      return
    }
    if (reason === 'selectOption') {
      // check wether added option contains "Add" in it
      if (typeof newValue === 'string' || !newValue) {
        return
      }
      if (newValue.isNew) {
        if (!newValue.inputValue) {
          return
        }
        createSpecialityOptionDispatch(
          createSpecialityOption({
            name: (newValue as SpecialityAutocompleteOption).inputValue as string,
            code: 'CREATED.' + ((newValue as SpecialityAutocompleteOption).inputValue as string).toLowerCase().replace(/ /g, '-'),
          })
        )
        return
      }
    }
    if (typeof newValue === 'string' || !newValue) {
      return
    }
    handleSetSpecialityValue(newValue)
  }

  return (
    <CreateConsultationStepLayout nextButtonDisabled={!values.speciality}>
      <SelectSpecialityStep
        onChange={({ reason }) => {
          if (reason) setValue('reason', reason)
        }}
        reasons={reasons}
        specialities={specialities}
        onInputChange={handleInputChange}
        onSpecialityChange={handleSpecialityChange}
        selectedSpeciality={selectedSpeciality}
      />
    </CreateConsultationStepLayout>
  )
}
